import React, { FC, useId, useMemo } from 'react';
import './FormGroup.scss';
import clsx from 'clsx';
import { ReadonlyHint } from '../ReadonlyIndicator';
import { FieldA11yContext, IFieldA11y, joinIds } from '../../context/FieldA11yContext';
import type { ControlSize } from '../../types/ControlSize';

export interface IFormGroup {
    /** Дочерние элементы */
    children: React.ReactNode | React.ReactNode[];
    /** Имя */
    label?: React.ReactNode;
    /** id текста подписи (по умолчанию — свой); поле группы ссылается на него `aria-labelledby` */
    labelId?: string;
    /** Подсказка */
    tooltip?: React.ReactNode;
    /** Текст справа от подписи; строка — пояснение поля (`aria-describedby`) */
    labelSecondary?: React.ReactNode;
    /** Сообщение под полем: при `invalid` — ошибка (`role="alert"`), иначе пояснение; поле ссылается на него `aria-describedby` */
    errorMessage?: React.ReactNode;
    /** Красная подсветка верхнего лейбла и сообщения об ошибке */
    invalid?: boolean;
    /** disabled состояние верхнего лейбла */
    disabled?: boolean;
    /** Красная подсветка labelSecondary */
    invalidLabelSecondary?: boolean;
    /** Дополнительный класс */
    className?: string;
    /** Обязательность */
    required?: boolean;
    /** Выравнивание по верху */
    verticalBottomAlign?: boolean;
    hideErrorMessage?: boolean;
    controlAdornment?: React.ReactNode;
    controlClassName?: string;
    readOnlyHint?: boolean;
    /**
     * Держать место под одну строку сообщения: слот `.rf-form-group__message` есть всегда (без текста — и при
     * `hideErrorMessage`), однострочная ошибка не меняет высоту группы. Слот стоит под полем в обеих ориентациях
     * (`verticalBottomAlign`), со счётчиком символов — на его строке.
     * @default false
     */
    reserveMessage?: boolean;
    /**
     * Размер полей группы: `md` — поле формы (40px, кегль 16), `sm` — компактное (32px, кегль 14) для плотных панелей
     * (свойства элемента); поле внутри — того же размера `sm`
     * @default 'md'
     */
    size?: Extract<ControlSize, 'sm' | 'md'>;
}

const FormGroup: FC<IFormGroup> = ({
    label,
    labelId: labelIdProp,
    labelSecondary,
    tooltip,
    children,
    errorMessage,
    className = '',
    required = false,
    invalid = false,
    disabled = false,
    invalidLabelSecondary = true,
    verticalBottomAlign = false,
    hideErrorMessage = false,
    controlAdornment,
    controlClassName,
    readOnlyHint = false,
    reserveMessage = false,
    size = 'md',
}: IFormGroup) => {
    const ownId = useId();
    const labelId = labelIdProp ?? `${ownId}label`;
    const messageId = `${ownId}message`;
    const secondaryId = `${ownId}secondary`;
    const showMessage = !hideErrorMessage && !!errorMessage;
    // У FormControl абзац под полем есть всегда, а текст в нём — только у ошибки: пустой в описание не берём.
    const describesMessage =
        showMessage && (invalid || typeof errorMessage === 'string' || typeof errorMessage === 'number');
    const secondaryText =
        !!label && (typeof labelSecondary === 'string' || typeof labelSecondary === 'number') && labelSecondary !== '';
    const labelText = typeof label === 'string' ? label : undefined;
    const fieldA11y = useMemo<IFieldA11y>(
        () => ({
            labelId: label ? labelId : undefined,
            labelText,
            describedBy: joinIds(describesMessage && messageId, secondaryText && secondaryId),
        }),
        [!!label, labelId, labelText, describesMessage, messageId, secondaryText, secondaryId],
    );

    // Ошибка объявляется при появлении: элемент с role="alert" монтируется заново, когда поле становится
    // невалидным (у FormControl абзац под полем есть всегда, меняется только текст).
    const message = showMessage ? (
        <p
            key={invalid ? 'error' : 'note'}
            id={messageId}
            role={invalid ? 'alert' : undefined}
            className={clsx('rf-form-group__message', !invalid && 'rf-form-group__message_no-invalid')}
        >
            {errorMessage}
        </p>
    ) : (
        reserveMessage && <p key="reserve" className="rf-form-group__message" />
    );
    const control = (
        <div className={clsx('rf-form-group__ctl', controlClassName)}>
            <FieldA11yContext value={fieldA11y}>{children}</FieldA11yContext>
            {controlAdornment}
        </div>
    );

    return (
        <ReadonlyHint disabled={!readOnlyHint}>
            <div
                className={clsx(
                    'rf-form-group',
                    size === 'sm' && 'rf-form-group--sm',
                    readOnlyHint && 'rf-form-group--readonly-hint',
                    reserveMessage && 'rf-form-group--reserve-message',
                    className,
                )}
                aria-label={readOnlyHint && typeof label === 'string' ? label : undefined}
            >
                <div className="rf-form-group_label__inner">
                    {label && (
                        <div
                            className={clsx(
                                'rf-form-group__label',
                                disabled && 'rf-form-group__label--disabled',
                                invalid && 'rf-form-group__label--invalid',
                            )}
                        >
                            {label && (
                                <span className="rf-form-group-fl" id={labelId}>
                                    {label}
                                </span>
                            )}
                            {required && <span className="rf-form-group-fl rf-form-group__required">*</span>}
                            {tooltip && (
                                <span className="rf-form-group-fl">
                                    {/* значок подсказки (ToolTipTitle) берёт подпись поля себе в имя */}
                                    <FieldA11yContext value={fieldA11y}>{tooltip}</FieldA11yContext>
                                </span>
                            )}
                            {!!labelSecondary && (
                                <span
                                    id={secondaryText ? secondaryId : undefined}
                                    className={clsx(
                                        'rf-form-group__label-secondary',
                                        invalidLabelSecondary && 'rf-form-group__message_no_invalid',
                                    )}
                                >
                                    {labelSecondary}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {verticalBottomAlign ? (
                    <div className={clsx('rf-form-group__inner', 'rf-form-group__verticalbottom')}>
                        {message}
                        {control}
                    </div>
                ) : (
                    <div className={clsx('rf-form-group__inner', 'rf-form-group__verticaltop')}>
                        {control}
                        {message}
                    </div>
                )}
            </div>
        </ReadonlyHint>
    );
};

export default FormGroup;
